// FORGE Pipeline Overview — complete onboarding funnel from Interest →
// Application → BOD Vote → Training → Year 1 Active.
//
// Aggregate only (no names). Answers "where is the pipeline right now"
// in under 10 seconds: volume by phase, conversion between phases,
// month-over-month velocity, bottleneck, and YoY delta.
//
// This page is the FORGE section's landing route (`/forge`). Detailed
// per-phase views live at /forge/interest, /forge/application, etc.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useAggregates,
  useMembers,
  usePipeline,
  MEMBERS_PAGE_LIMIT,
} from "../../hooks/useDashboard";
import type { MemberRow, PipelineStageRow } from "../../hooks/useDashboard";
import { usePipelineWaterfall, useBenchmarks } from "../../hooks/useForge";
import type { WaterfallStepRow } from "../../hooks/useForge";
import KPICard from "../../components/dashboard/KPICard";
import TruncationNotice from "../../components/shared/TruncationNotice";
import ChartCard from "../../components/dashboard/ChartCard";
import GainLossChart from "../../components/dashboard/GainLossChart";
import type { MonthlyGainLoss } from "../../components/dashboard/GainLossChart";
import YoYMembershipChart from "../../components/dashboard/YoYMembershipChart";
import type { YoYRow } from "../../components/dashboard/YoYMembershipChart";
import { useYoYHistory } from "../../hooks/useDashboard";
import StatusBadge from "../../components/dashboard/StatusBadge";
import type { WaterfallData } from "../../components/dashboard/PipelineWaterfall";
import PipelineProcessFlow from "../../components/dashboard/PipelineProcessFlow";
import DropOffFunnel from "../../components/forge/DropOffFunnel";
import { LiveIndicator } from "../../components/shared/LiveIndicator";
import BenchmarkIndicator from "../../components/dashboard/BenchmarkIndicator";
import RecommendationCard from "../../components/dashboard/RecommendationCard";
import type { RecommendationCardProps } from "../../components/dashboard/RecommendationCard";
import InsightCards from "../../components/ai/InsightCards";
import { useInsights } from "../../hooks/useAI";

// ── Date range types ──────────────────────────────────────────────────────────
//
// Only three periods are actually supported by `/api/forge/pipeline-waterfall`:
// 'all', 'ytd', 'l12m'. Prior versions of this page exposed six options
// (day/week/month/quarter/year/all) but half of them silently collapsed to
// 'all' — the toggle appeared to do something but didn't. Expanded periods
// require a backend change; until then the UI reflects what the API can
// actually honor.

type DateRange = "all" | "ytd" | "l12m";

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: "l12m", label: "Last 12 Months" },
  { value: "ytd", label: "YTD" },
  { value: "all", label: "All Time" },
];

function toWaterfallPeriod(range: DateRange): "all" | "ytd" | "l12m" {
  return range;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function asString(v: unknown, fallback = ""): string {
  if (typeof v === "string") return v;
  return fallback;
}

/** Map raw pipeline_stage strings to canonical display names — used for fallback active count */
const STAGE_MAP: Record<string, { display: string; order: number }> = {
  New: { display: "New / Contacted", order: 1 },
  Contacted: { display: "New / Contacted", order: 1 },
  "New/Contacted": { display: "New / Contacted", order: 1 },
  "New / Contacted": { display: "New / Contacted", order: 1 },
  Background: { display: "Background Cleared", order: 2 },
  "Background Cleared": { display: "Background Cleared", order: 2 },
  "Background Check": { display: "Background Cleared", order: 2 },
  AIS: { display: "AIS Attended", order: 3 },
  "AIS Attended": { display: "AIS Attended", order: 3 },
  "AIS Session": { display: "AIS Attended", order: 3 },
  Application: { display: "Continued to App", order: 4 },
  "Application Received": { display: "Continued to App", order: 4 },
  "Continued to App": { display: "Continued to App", order: 4 },
  "Continued to Application": { display: "Continued to App", order: 4 },
  OEMS: { display: "OEMS Eligible", order: 5 },
  "OEMS Eligible": { display: "OEMS Eligible", order: 5 },
  Physical: { display: "Physical Passed", order: 6 },
  "Physical Passed": { display: "Physical Passed", order: 6 },
  "Physical Exam": { display: "Physical Passed", order: 6 },
  BOD: { display: "BOD Approved", order: 7 },
  "BOD Approved": { display: "BOD Approved", order: 7 },
  "Board Approved": { display: "BOD Approved", order: 7 },
  Complete: { display: "Complete", order: 8 },
  Completed: { display: "Complete", order: 8 },
};

/** Sum all non-complete pipeline stages to get total active applicants (fallback) */
function countActivePipeline(rawStages: PipelineStageRow[]): number {
  return rawStages
    .filter((row) => {
      const stage = row.pipeline_stage ?? "";
      const mapped = STAGE_MAP[stage];
      return !mapped || mapped.display !== "Complete";
    })
    .reduce((sum, row) => sum + row.count, 0);
}

/** Build rolling N-month gain/loss data from member rows */
function buildMonthlyGainLoss(
  members: MemberRow[],
  numMonths = 12,
): MonthlyGainLoss[] {
  const now = new Date();
  const months: MonthlyGainLoss[] = [];

  for (let i = numMonths - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth(); // 0-indexed

    let joined = 0;
    let separated = 0;

    for (const m of members) {
      const joinedDate = m.joined_date
        ? new Date(asString(m.joined_date))
        : null;
      const sepDate = m.separation_date
        ? new Date(asString(m.separation_date))
        : null;

      if (
        joinedDate &&
        joinedDate.getFullYear() === year &&
        joinedDate.getMonth() === month
      ) {
        joined++;
      }
      if (
        sepDate &&
        sepDate.getFullYear() === year &&
        sepDate.getMonth() === month
      ) {
        separated++;
      }
    }

    const monthLabel = d.toLocaleString("en-US", { month: "short" });
    const yearLabel = String(year).slice(2);
    months.push({
      month: `${monthLabel} '${yearLabel}`,
      joined,
      separated,
      net: joined - separated,
    });
  }

  return months;
}

/** Filter member rows to the selected date range for the gain/loss chart. */
function filterMembersByRange(
  members: MemberRow[],
  range: DateRange,
): MemberRow[] {
  if (range === "all") return members;
  const now = new Date();
  const cutoff =
    range === "ytd"
      ? new Date(now.getFullYear(), 0, 1)
      : new Date(now.getFullYear(), now.getMonth() - 12, 1); // l12m
  return members.filter((m) => {
    const joinedDate = m.joined_date ? new Date(asString(m.joined_date)) : null;
    const sepDate = m.separation_date
      ? new Date(asString(m.separation_date))
      : null;
    return (
      (joinedDate && joinedDate >= cutoff) || (sepDate && sepDate >= cutoff)
    );
  });
}

/** Chart-window months based on the selected range. */
function chartMonthsForRange(range: DateRange): number {
  if (range === "l12m") return 12;
  if (range === "ytd") return Math.max(3, new Date().getMonth() + 1);
  return 12;
}

// ── YoY constants ─────────────────────────────────────────────────────────────

/** Statuses that count as "active" members (not just technically on the books) */
const ACTIVE_STATUSES = new Set([
  "Running",
  "Probationary",
  "Associate (Active)",
  "Life Member (Active)",
  "Probationary (Cadet)",
  "Probationary (HS)",
  "Associate (Probationary)",
]);

/** "Running" status = actually staffing apparatus */
const RUNNING_STATUS = "Running";

/** Build year-over-year chart data with accurate active/running counts at year-end.
 *
 * The key insight: Status_New on each row is the CURRENT status, not the status
 * at a given year-end. To approximate "who was active at year-end Y":
 *   1. Member joined on or before Dec 31 of Y
 *   2. Member was not yet separated as of Dec 31 of Y (no sep date, or sep after year-end)
 *   3. Member is CURRENTLY in an ACTIVE_STATUS (filters out Life Member Inactive,
 *      Honorary Members, Inactive-*, Associate Inactive who never get a sep_date)
 *
 * This is an approximation — we're using current status as a proxy for whether
 * the member was ever "real" (i.e., not a ceremonial/inactive record).
 * For historical years this is the best we can do without status snapshot history.
 */
function _buildYoYData(members: MemberRow[], range?: DateRange): YoYRow[] {
  const currentYear = new Date().getFullYear();

  // Full 17-year window (2010–present). For short date ranges (day/week/month/quarter/year)
  // we still show the full arc so the annotation context is always visible.
  const years = [
    2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021,
    2022, 2023, 2024, 2025, 2026,
  ].filter((y) => y <= currentYear);

  // Narrow the YoY chart window when the caller has picked a scoped range.
  // Only 'all' should render the full 2010-onward history.
  const startYear =
    range && range !== "all" ? currentYear - (range === "l12m" ? 1 : 0) : 2010;

  const filteredYears = years.filter((y) => y >= startYear);

  const rows: YoYRow[] = [];

  for (const year of filteredYears) {
    let joined = 0;
    let separated = 0;

    const yearEnd = new Date(year, 11, 31);

    for (const m of members) {
      const joinedDate = m.joined_date
        ? new Date(asString(m.joined_date))
        : null;
      const sepDate = m.separation_date
        ? new Date(asString(m.separation_date))
        : null;

      if (joinedDate && joinedDate.getFullYear() === year) joined++;
      if (sepDate && sepDate.getFullYear() === year) separated++;
    }

    // Count members ACTIVE at year-end:
    //   - joined on or before Dec 31 of this year
    //   - not yet separated as of Dec 31 of this year
    //   - currently in a status that is a real active status (not Inactive/Honorary/Life Inactive)
    const totalActive = members.filter((m) => {
      const joinedDate = m.joined_date
        ? new Date(asString(m.joined_date))
        : null;
      const sepDate = m.separation_date
        ? new Date(asString(m.separation_date))
        : null;
      if (!joinedDate || joinedDate > yearEnd) return false;
      if (sepDate && sepDate <= yearEnd) return false;
      const statusNew = asString(
        m.status_new ?? (m["status_new"] as string | undefined) ?? "",
      );
      return ACTIVE_STATUSES.has(statusNew);
    }).length;

    // Count members RUNNING at year-end (staffing apparatus):
    //   - same join/sep criteria
    //   - currently in "Running" status
    const totalRunning = members.filter((m) => {
      const joinedDate = m.joined_date
        ? new Date(asString(m.joined_date))
        : null;
      const sepDate = m.separation_date
        ? new Date(asString(m.separation_date))
        : null;
      if (!joinedDate || joinedDate > yearEnd) return false;
      if (sepDate && sepDate <= yearEnd) return false;
      const statusNew = asString(
        m.status_new ?? (m["status_new"] as string | undefined) ?? "",
      );
      return statusNew === RUNNING_STATUS;
    }).length;

    const net = joined - separated;
    rows.push({
      year,
      joined,
      separated,
      net,
      totalActiveAtEnd: Math.max(0, totalActive),
      totalRunning: Math.max(0, totalRunning),
    });
  }

  return rows.filter(
    (r) => r.joined > 0 || r.separated > 0 || r.totalActiveAtEnd > 0,
  );
}

// ── KPI helpers ───────────────────────────────────────────────────────────────

/**
 * Color for avg_days_to_bod
 * Target: <45 = green, 45-70 = amber, >70 = red
 */
function bodColor(days: number): "green" | "amber" | "red" {
  if (days < 45) return "green";
  if (days <= 70) return "amber";
  return "red";
}

/**
 * Color for active members
 * Target: 200+ = green, 150-199 = amber, <150 = red
 */
function activeMemberColor(count: number): "green" | "amber" | "red" {
  if (count >= 200) return "green";
  if (count >= 150) return "amber";
  return "red";
}

/**
 * Color for net change
 * >0 = green, 0 = amber, <0 = red
 */
function netChangeColor(net: number): "green" | "amber" | "red" {
  if (net > 0) return "green";
  if (net === 0) return "amber";
  return "red";
}

// LiveIndicator moved to components/shared/LiveIndicator.tsx

// ── Date Range Toggle — pill buttons ─────────────────────────────────────────

function DateRangeToggle({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (v: DateRange) => void;
}) {
  return (
    <div className="flex items-center gap-0.5 bg-theme-overlay border border-theme-subtle rounded-full p-1 flex-wrap">
      {DATE_RANGE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={[
            "px-3 py-1.5 text-xs font-semibold rounded-full transition-all min-h-[32px]",
            value === opt.value
              ? "bg-[#0E403A] text-white shadow-sm"
              : "text-theme-secondary hover:text-theme-primary hover:bg-theme-overlay",
          ].join(" ")}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const INTERVAL = 60_000;

  const [dateRange, setDateRange] = useState<DateRange>("all");
  const waterfallPeriod = toWaterfallPeriod(dateRange);

  // Data fetching
  const {
    data: aggregatesData,
    isLoading: aggLoading,
    lastUpdatedAt: aggUpdated,
  } = useAggregates(INTERVAL);
  const { data: insightsData, isLoading: insightsLoading } =
    useInsights("overview");
  const { data: pipelineData, isLoading: pipeLoading } = usePipeline(INTERVAL);
  // Limit 5,000 = the API hard maximum. Live: 1,895 rows in dash_members
  // (2026-08-27), 2.6x headroom. This site was already complete; it now reads
  // the shared constant so the three member call sites cannot drift apart.
  const { data: membersData, isLoading: membersLoading } = useMembers(
    { limit: MEMBERS_PAGE_LIMIT },
    INTERVAL,
  );
  const {
    data: waterfallRaw,
    isLoading: waterfallLoading,
    error: waterfallError,
    refresh: waterfallRefresh,
  } = usePipelineWaterfall(waterfallPeriod, 300_000);
  const { data: benchmarksData, isLoading: benchmarksLoading } =
    useBenchmarks(300_000);

  const agg = aggregatesData?.aggregates ?? {};

  // ── KPI values ─────────────────────────────────────────────────────────────
  const activeMemberCount = asNumber(agg["active_member_count"]);
  const totalMemberCount = asNumber(agg["total_member_count"]);
  const ytdJoined = asNumber(agg["ytd_joined"]);
  const ytdSeparated = asNumber(agg["ytd_separated"]);
  const netChangeYtd = ytdJoined - ytdSeparated;
  const avgDaysToBod = asNumber(agg["avg_days_to_bod"]);
  const avgDaysToBod2023 = asNumber(agg["avg_days_to_bod_2023plus"]);

  const pipelineActive = useMemo(() => {
    const fromAgg = asNumber(agg["pipeline_active_current"]);
    if (fromAgg > 0) return fromAgg;
    if (!pipelineData?.pipeline) return 0;
    return countActivePipeline(pipelineData.pipeline);
  }, [agg, pipelineData]);

  // ── Monthly gain/loss data ───────────────────────────────────────────────────
  const monthlyData = useMemo<MonthlyGainLoss[]>(() => {
    if (!membersData?.members || membersData.members.length === 0) return [];
    const numMonths = chartMonthsForRange(dateRange);
    const members =
      dateRange === "all"
        ? membersData.members
        : filterMembersByRange(membersData.members, dateRange);
    return buildMonthlyGainLoss(
      members.length > 0 ? membersData.members : [],
      numMonths,
    );
  }, [membersData, dateRange]);

  // ── Trend indicators ────────────────────────────────────────────────────────
  const netTrend =
    netChangeYtd !== 0
      ? {
          direction: (netChangeYtd >= 0 ? "up" : "down") as "up" | "down",
          label: `${Math.abs(netChangeYtd)} YTD`,
          positiveIsGood: true,
        }
      : undefined;

  const bodAccent =
    avgDaysToBod > 0 ? bodColor(avgDaysToBod) : ("neutral" as const);
  const activeMembersAccent =
    activeMemberCount > 0
      ? activeMemberColor(activeMemberCount)
      : ("neutral" as const);
  const netAccent = aggLoading
    ? ("neutral" as const)
    : netChangeColor(netChangeYtd);

  // ── YoY chart data (from server-computed snapshot) ──────────────────────────
  const { data: yoyHistoryData, isLoading: yoyLoading } = useYoYHistory();
  const yoyData = useMemo<YoYRow[]>(() => {
    if (!yoyHistoryData?.years) return [];
    return yoyHistoryData.years.map((row) => ({
      year: row.year,
      joined: row.joined_that_year,
      separated: row.separated_that_year,
      net: row.joined_that_year - row.separated_that_year,
      totalActiveAtEnd: row.total_active,
      totalRunning: row.total_running,
      isEstimated: (row.is_estimated ?? 0) === 1,
    }));
  }, [yoyHistoryData]);

  // ── Waterfall data — map API response to WaterfallData ─────────────────────
  const waterfallData = useMemo<WaterfallData | null>(() => {
    if (!waterfallRaw) return null;
    return {
      steps: waterfallRaw.steps.map((s: WaterfallStepRow) => ({
        step: s.step,
        name: s.name,
        count: s.count,
        phase: s.phase,
        dropCount: s.dropCount ?? undefined,
        dropPct: s.dropPct ?? undefined,
      })),
      totalTypeform: waterfallRaw.totalTypeform,
      totalBodApproved: waterfallRaw.totalBodApproved,
      totalTrackingPool: waterfallRaw.totalTrackingPool,
      totalReleasedForAssignment: waterfallRaw.totalReleasedForAssignment,
      periodLabel: waterfallRaw.periodLabel,
    };
  }, [waterfallRaw]);

  // Waterfall error string — hook already returns string | null
  const waterfallErrorMsg: string | null = waterfallError ?? null;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
            FORGE
          </p>
          <h1 className="text-2xl font-black text-theme-primary tracking-tight">
            Pipeline Overview
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Where the onboarding funnel is right now — Interest → Application →
            BOD Vote → Training → Year 1 Active.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <LiveIndicator lastUpdatedAt={aggUpdated} />
        </div>
      </div>

      {/* ── Row-completeness notice ───────────────────────────────────
          This page was already asking for the whole table (5,000 vs 1,895 live
          rows on 2026-08-27), but "complete" is now something the response
          PROVES rather than something the page assumes. If dash_members grows
          past the 5,000 hard cap, the reader is told here instead of quietly
          reading a partial roster. */}
      <TruncationNotice
        response={membersData}
        label="member"
        hint="Retention and tenure figures below are computed from the member rows that loaded."
      />

      {/* ── Section 1: Top-line KPIs ──────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-bold text-theme-muted uppercase tracking-widest mb-4">
          Membership Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {/* KPI 1: Active Members */}
          <Link
            to="/membership"
            className="block hover:opacity-90 transition-opacity"
          >
            <KPICard
              label="Active Members"
              value={aggLoading ? "—" : activeMemberCount}
              subtitle={
                totalMemberCount > 0
                  ? `${activeMemberCount.toLocaleString()} of ${totalMemberCount.toLocaleString()} historical · target 200+`
                  : "target: 200+ members"
              }
              accent={aggLoading ? "neutral" : activeMembersAccent}
              isLoading={aggLoading}
            />
          </Link>

          {/* KPI 2: Net Change YTD */}
          <KPICard
            label="Net Change YTD"
            value={
              aggLoading
                ? "—"
                : netChangeYtd > 0
                  ? `+${netChangeYtd}`
                  : String(netChangeYtd)
            }
            subtitle={`${ytdJoined.toLocaleString()} joined · ${ytdSeparated.toLocaleString()} separated`}
            accent={netAccent}
            trend={netTrend}
            isLoading={aggLoading}
          />

          {/* KPI 3: Pipeline Active */}
          <Link
            to="/applicants"
            className="block hover:opacity-90 transition-opacity"
          >
            <KPICard
              label="Pipeline Active"
              value={pipeLoading && aggLoading ? "—" : pipelineActive}
              subtitle="current-year applicants in process"
              accent="neutral"
              isLoading={pipeLoading && aggLoading}
            />
          </Link>

          {/* KPI 4: Avg Days to BOD (2023+ era).
              Pre-2023 records are sparse and skew the all-time average; the
              2023+ aggregate is the honest number to track. We fall through to
              the all-time value only when the 2023+ window has no data yet.
              The "Q1-2027 target of ≤45 days" this used to name was removed
              2026-08-26 (Riley, 2026-08-25): it is the OLD 2025 objective the
              2026 OKRs replaced. The measurement is real; the target was not. */}
          <Link
            to="/applicants"
            className="block hover:opacity-90 transition-opacity"
          >
            <KPICard
              label="Avg Days to BOD"
              value={
                aggLoading
                  ? "—"
                  : avgDaysToBod2023 > 0
                    ? Math.round(avgDaysToBod2023)
                    : avgDaysToBod > 0
                      ? Math.round(avgDaysToBod)
                      : "No data"
              }
              subtitle={`Application → BOD vote · 2023+ cohort · no approved target`}
              accent={
                avgDaysToBod2023 > 0
                  ? bodColor(avgDaysToBod2023)
                  : avgDaysToBod > 0
                    ? bodAccent
                    : "neutral"
              }
              isLoading={aggLoading}
            />
          </Link>
        </div>
      </section>

      {/* ── AI Insight Cards (shown when insights are available) ──────── */}
      {(insightsLoading ||
        (insightsData?.insights && insightsData.insights.length > 0)) && (
        <section>
          <h2 className="text-xs font-bold text-theme-muted uppercase tracking-widest mb-4">
            AI Insights
          </h2>
          <InsightCards
            insights={insightsData?.insights}
            isLoading={insightsLoading}
            maxCards={3}
          />
        </section>
      )}

      {/* ── Date Range Toggle ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-xs text-theme-muted">
          Filter pipeline, gain/loss chart, and membership table:
        </p>
        <DateRangeToggle value={dateRange} onChange={setDateRange} />
      </div>

      {/* ── Section 1.5: Where Applicants Are Lost (honest drop-off) ─── */}
      <section>
        <DropOffFunnel />
      </section>

      {/* ── Section 2: Horizontal Phase Process Flow ─────────────────── */}
      <section>
        <ChartCard
          title="FORGE Onboarding Pipeline — Interest to First Duty"
          subtitle={
            waterfallData
              ? `${waterfallData.periodLabel} · ${waterfallData.totalTypeform.toLocaleString()} entered · ${waterfallData.totalReleasedForAssignment.toLocaleString()} reached first duty · click any phase to expand step detail`
              : "5 phases · 30 steps tracked · click a phase card to drill down"
          }
          headerRight={
            waterfallLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : waterfallErrorMsg ? (
              <StatusBadge label="Load error" variant="red" />
            ) : !waterfallData ? (
              <StatusBadge label="Sync pending" variant="yellow" />
            ) : (
              <StatusBadge
                label={`${waterfallData.steps.length} steps`}
                variant="blue"
              />
            )
          }
          minHeight={220}
        >
          <PipelineProcessFlow
            aggregates={agg}
            steps={waterfallRaw?.steps ?? []}
            period={waterfallData?.periodLabel ?? waterfallPeriod}
            isLoading={aggLoading}
            error={waterfallErrorMsg}
            onRetry={waterfallRefresh}
          />
        </ChartCard>
      </section>

      {/* ── Section 3: Monthly Gain/Loss ─────────────────────────────── */}
      <section>
        <ChartCard
          title="Monthly Membership Gain / Loss"
          subtitle="Green bars: joined · Red bars: separated · Net annotated per group · Blue dashed line: cumulative YTD net"
          headerRight={
            membersLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : membersData && membersData.count === 0 ? (
              <StatusBadge label="Data syncing..." variant="yellow" />
            ) : membersData ? (
              <StatusBadge
                label={`${membersData.count.toLocaleString()} members`}
                variant="muted"
              />
            ) : null
          }
          minHeight={300}
        >
          <GainLossChart
            data={monthlyData}
            isLoading={membersLoading}
            showNetLabels
          />
        </ChartCard>
      </section>

      {/* ── Section 4: Year-over-Year Trajectory Chart ───────────────── */}
      <section>
        <ChartCard
          title="Membership Trajectory (Year-over-Year)"
          subtitle="Active membership trend vs. 200+ staffing target — area = total active at year-end, bars = net annual change"
          minHeight={0}
        >
          <YoYMembershipChart rows={yoyData} isLoading={yoyLoading} />
          {/* Insight callout — computed from real YoY data */}
          {!yoyLoading &&
            yoyData.length > 1 &&
            (() => {
              const withData = yoyData.filter((r) => r.totalActiveAtEnd > 0);
              if (withData.length === 0) return null;

              const current = withData[withData.length - 1];
              const currentRunning = current.totalRunning ?? 0;

              // Pre-change baseline: 2017 or earliest row before 2018
              const preChangeRow =
                withData.find((r) => r.year === 2017) ??
                withData.filter((r) => r.year < 2018).slice(-1)[0] ??
                withData[0];
              const preChangeYear = preChangeRow.year;
              const preChangeMembers = preChangeRow.totalActiveAtEnd;

              // Peak active members across all years
              const peakRow = withData.reduce((best, r) =>
                r.totalActiveAtEnd > best.totalActiveAtEnd ? r : best,
              );
              const peakYear = peakRow.year;
              const peakMembers = peakRow.totalActiveAtEnd;

              // Decline % from peak to current
              const declinePct =
                peakMembers > 0
                  ? Math.round(
                      ((peakMembers - current.totalActiveAtEnd) / peakMembers) *
                        100,
                    )
                  : 0;

              // Running roster plateau detection
              const runningRows = withData.filter(
                (r) => (r.totalRunning ?? 0) > 0,
              );
              const plateauYear =
                runningRows.length >= 2
                  ? runningRows.reduce((prev, r) =>
                      (r.totalRunning ?? 0) >= (prev.totalRunning ?? 0)
                        ? r
                        : prev,
                    ).year
                  : current.year;

              const has2018 = withData.some((r) => r.year === 2018);

              return (
                <div className="mt-4 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 p-4 text-sm space-y-2">
                  {has2018 && (
                    <div className="flex items-start gap-2">
                      <span className="inline-block h-4 w-1 mt-0.5 rounded bg-red-500 shrink-0" />
                      <div>
                        <strong className="text-red-700 dark:text-red-400">
                          July 2018 — 56-hour work week introduced.
                        </strong>
                        <p className="text-zinc-600 dark:text-zinc-400 mt-1">
                          Paid personnel began occupying volunteer time slots.
                          This change created a &ldquo;loss of purpose&rdquo;
                          for volunteers — why staff when a paid employee is
                          already there? The trajectory clearly shifts from this
                          point.
                        </p>
                      </div>
                    </div>
                  )}

                  <div
                    className={
                      has2018
                        ? "pt-2 border-t border-zinc-200 dark:border-zinc-800"
                        : ""
                    }
                  >
                    <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                      <span className="font-semibold">Full timeline:</span>{" "}
                      Active membership grew from{" "}
                      <span className="font-semibold">{preChangeMembers}</span>{" "}
                      ({preChangeYear}) to peak of{" "}
                      <span className="font-semibold">{peakMembers}</span> (
                      {peakYear}),{" "}
                      {declinePct > 0 ? (
                        <>
                          then declined{" "}
                          <span className="font-semibold text-red-600 dark:text-red-400">
                            {declinePct}%
                          </span>{" "}
                          to{" "}
                          <span className="font-semibold">
                            {current.totalActiveAtEnd}
                          </span>{" "}
                          today.
                        </>
                      ) : (
                        <>
                          remaining at{" "}
                          <span className="font-semibold">
                            {current.totalActiveAtEnd}
                          </span>{" "}
                          today.
                        </>
                      )}
                      {currentRunning > 0 && (
                        <>
                          {" "}
                          The{" "}
                          <span className="font-semibold">
                            Running roster
                          </span>{" "}
                          (actually staffing apparatus){" "}
                          {plateauYear < current.year ? (
                            <>
                              plateaued at{" "}
                              <span className="font-semibold text-amber-600 dark:text-amber-400">
                                {currentRunning}
                              </span>{" "}
                              since {plateauYear} —
                            </>
                          ) : (
                            <>
                              stands at{" "}
                              <span className="font-semibold">
                                {currentRunning}
                              </span>{" "}
                              —
                            </>
                          )}{" "}
                          a capacity ceiling that reflects the loss of volunteer
                          incentive post-2018.
                        </>
                      )}{" "}
                      {current.totalActiveAtEnd < 200 ? (
                        <span className="text-amber-600 dark:text-amber-400 font-semibold">
                          The department is {200 - current.totalActiveAtEnd}{" "}
                          members below the 200-member staffing target.
                        </span>
                      ) : (
                        <span className="text-green-600 dark:text-green-400 font-semibold">
                          The department is meeting the 200-member staffing
                          target.
                        </span>
                      )}{" "}
                      91% of Virginia public safety leaders report declining VFF
                      membership [NVFC 2023].
                    </p>
                  </div>
                </div>
              );
            })()}
        </ChartCard>
      </section>

      {/* ── Section 5: How We Compare — National Benchmarks ──────────── */}
      <section>
        <ChartCard
          title="How We Compare — National Benchmarks"
          subtitle="DCVFD vs. national VFD/EMS averages and top-quartile targets · Sources: NFPA, NVFC, USFA, IAFC research"
          minHeight={0}
        >
          {benchmarksLoading ? (
            <div className="space-y-4 py-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-1.5">
                  <div className="skeleton h-3 w-28 rounded" />
                  <div className="skeleton h-3 w-full rounded" />
                </div>
              ))}
            </div>
          ) : benchmarksData?.benchmarks ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 py-1">
              {benchmarksData.benchmarks
                .filter((b) => b.currentValue !== null)
                .map((b) => (
                  <BenchmarkIndicator
                    key={b.metric}
                    current={b.currentValue as number}
                    nationalAverage={b.nationalAverage}
                    topQuartile={b.topQuartile}
                    unit={b.unit === "ratio" ? "count" : b.unit}
                    direction={b.direction}
                    label={b.label}
                    source={b.source}
                    note={b.note}
                  />
                ))}
            </div>
          ) : (
            <p className="text-sm text-theme-muted py-4 text-center">
              Benchmark data unavailable
            </p>
          )}
          <div className="mt-4 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 px-4 py-3 text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
            <span className="font-semibold text-zinc-700 dark:text-zinc-300">
              National context:{" "}
            </span>
            91% of Virginia public safety leaders report declining VFF
            membership [NVFC 2023]. The interventions required to close these
            gaps are well-documented and low-cost — see the recommendations
            below.
          </div>
        </ChartCard>
      </section>

      {/* ── Section 6: Top Recommendations ──────────────────────────── */}
      {(() => {
        const TOP_RECS: RecommendationCardProps[] = [
          // RETRACTED 2026-08-25 by Riley (R&R Director), removed here 2026-08-26.
          // The former rank-1 card recommended observer rides within 14 days of BOD
          // approval with a retention benefit attached. Riley withdrew it on SAFETY
          // grounds: those rides are governed by the Chief's Running Rules Art. II
          // (18+, signed release, per-ride permission from the Chief or DCO, one ride
          // only) and DCVFD carries four 17-year-old cadets. This page cannot see who
          // is reading it or how old they are. The same claim was stripped from the
          // AI prompts on 2026-08-25, but a prompt only fires when someone asks; this
          // card rendered for everyone who opened the page, ranked first.
          // Do not reinstate without a written ruling from the R&R Director.
          {
            rank: 1,
            title:
              "Structured mentorship — paired within 24 hours of BOD, 90-day program",
            impact: "high",
            effort: "low",
            cost: 0,
            expectedOutcome:
              'Addresses the "feeling unneeded" attrition driver in the first 90 days',
            evidence:
              "93% of Gen Z volunteers cite mentor relationships as primary reason for staying beyond year one [NVFC 2020 n=1,000+]",
            source:
              "docs/kb/recommendations-ranked.md · NVFC Retention Research 2020",
          },
          {
            rank: 2,
            title:
              "Fix AIS drop-off: personalized outreach within 24 hours of application",
            impact: "very-high",
            effort: "low",
            cost: 200,
            expectedOutcome:
              "Moving AIS attendance from 40% to 65% adds ~15 BOD approvals per year — the single highest-leverage improvement available",
            evidence:
              "79% of the public has never been asked to volunteer; AIS is the conversion event; no-shows = paid leads wasted",
            source:
              "docs/kb/recommendations-ranked.md · NVFC Make Me A Firefighter · dashboard-narrative.md",
          },
        ];
        return (
          <section>
            <div className="flex items-center justify-between gap-2 mb-4">
              <div>
                <h2 className="text-xs font-bold text-theme-muted uppercase tracking-widest">
                  Top Recommendations
                </h2>
                <p className="text-xs text-theme-muted mt-0.5">
                  Research-backed actions ranked by (impact × evidence) ÷ cost ·
                  Full list: docs/kb/recommendations-ranked.md
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {TOP_RECS.map((rec) => (
                <RecommendationCard key={rec.rank} {...rec} />
              ))}
            </div>
          </section>
        );
      })()}
    </div>
  );
}
